import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import {
  addPageData,
  queryMinLoanAmountRequest,
  navigateWizard,
  fetchCapOnLoan,
  setLoadingState,
} from '../../actions';
import IneligibleLoanAmountScreen from '../../components/IneligibleLoanAmountScreen';
import Loading from '../../components/Loading';
import LoanAmountInputPreload from '../../components/LoanAmountInputPreload';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import { safeParseFloat } from '../../lib/helpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodShortTitle } from '../../lib/periodUtils';
import {
  periodPropTypes,
  otherFundsPropTypes,
  loanPeriodAmountPropTypes,
  capOnLoanPropTypes,
} from '../../reducers/propTypes';
import {
  periodSelector,
  periodCurrencySelector,
  otherFundsSelector,
  capOnLoanSelector,
  eligibilityIdSelector,
  isFullTimeCourseSelector,
  minLoanAmountSelector,
  fundsRequiredSelector,
  loadingSelector,
} from '../../reducers/selectors';
import './index.scss';

const LoanAmountPage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  currentLoanPeriod,
  period,
  minLoanAmount,
  queryMinLoanAmount,
  dispatchFetchCapOnLoan,
  eligibilityId,
  currency,
  otherFunds,
  capOnLoan,
  isFullTimeCourse,
  seenDepositPage,
  isLoading,
}) => {
  const {
    amount: currentLoanPeriodAmount,
    personalSavings: currentPersonalSavings,
    relativesContribution: currentRelativesContribution,
  } = currentLoanPeriod;
  const [amount, setAmount] = useState(currentLoanPeriodAmount);
  const [personalSavings, setPersonalSavings] = useState(currentPersonalSavings);
  const [relativesContribution, setRelativesContribution] = useState(currentRelativesContribution);

  useEffect(() => {
    setLoadingState(true);
    if ((!capOnLoan.maxLoanAmount && capOnLoan.maxLoanAmount !== 0) || !minLoanAmount) {
      queryMinLoanAmount(eligibilityId);
      dispatchFetchCapOnLoan();
    }
  }, [eligibilityId, queryMinLoanAmount, minLoanAmount, dispatchFetchCapOnLoan, capOnLoan]);

  const handleSubmit = () => {
    analytics.sendEvent('Click - Next', 'loan-amount');

    const currentLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
      'currentLoanPeriod',
      {
        personalSavings,
        relativesContribution,
        amount,
        currency,
      },
      'currentLoanPeriod',
      {
        personalSavings: `${safeParseFloat(personalSavings, 0)}`,
        relativesContribution: `${safeParseFloat(relativesContribution, 0)}`,
        amount,
        currency,
      },
    );

    dispatchAddPageData(currentLoanPeriodPayload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleClick = event => {
    event.preventDefault();
    if (seenDepositPage) {
      dispatchWizardNav({ dir: 'back' });
    }
    dispatchWizardNav({ dir: 'back' });
  };

  const { costOfAttendance, costOfTuition } = period;
  const { scholarship, sponsorship, loans } = otherFunds;
  const { period: scholarshipPeriod } = scholarship;
  const { amount: scholarshipPeriodAmount } = scholarshipPeriod;

  const { period: sponsorshipPeriod } = sponsorship;
  const { amount: sponsorshipPeriodAmount } = sponsorshipPeriod;

  const { period: loansPeriod } = loans;
  const { amount: loansPeriodAmount } = loansPeriod;

  const totalOtherFunds = useMemo(() => {
    const parsedScholarshipPeriodAmount = safeParseFloat(scholarshipPeriodAmount, 0);
    const parsedSponsorshipPeriodAmount = safeParseFloat(sponsorshipPeriodAmount, 0);
    const parsedLoansPeriodAmount = safeParseFloat(loansPeriodAmount, 0);

    return parsedScholarshipPeriodAmount + parsedSponsorshipPeriodAmount + parsedLoansPeriodAmount;
  }, [scholarshipPeriodAmount, sponsorshipPeriodAmount, loansPeriodAmount]);

  const involvedCosts = useMemo(
    () => (isFullTimeCourse ? parseFloat(costOfAttendance) : parseFloat(costOfTuition)),
    [isFullTimeCourse, costOfAttendance, costOfTuition],
  );

  const requiredAmount = useMemo(() => involvedCosts - totalOtherFunds, [
    involvedCosts,
    totalOtherFunds,
  ]);

  const title = `Great! Let's look at your budget for ${periodShortTitle(period)}`;
  const lowerThanMinimumAmountTitle =
    'We cannot offer you a loan as the loan amount you qualify for is lower than our minimum loan amount for this course';

  const socialProofPoints = [
    'Most applicants get 65% of their studies funded by Prodigy Finance.',
    'On average, they also contribute $23,639 from their personal savings, and $5,000 in the form of a contribution from friends and family.',
    '55% of our customers are either employed full-time or part-time when they apply.',
  ];

  return (
    <div>
      {isLoading && (
        <section className="container container--space-y text-center">
          <Loading />
        </section>
      )}
      {!isLoading && capOnLoan.maxLoanAmount >= minLoanAmount && (
        <PageLayout title={title} socialProofText={socialProofPoints}>
          <LoanAmountInputPreload
            handleSubmit={handleSubmit}
            currency={currency}
            amount={amount}
            onAmountChange={setAmount}
            personalSavings={personalSavings}
            onPersonalSavingsChange={setPersonalSavings}
            relativesContribution={relativesContribution}
            onRelativesContributionChange={setRelativesContribution}
            minLoanAmount={minLoanAmount}
            otherFunds={totalOtherFunds}
            requiredAmount={requiredAmount}
            capOnLoan={capOnLoan}
          />
        </PageLayout>
      )}
      {!isLoading && capOnLoan.maxLoanAmount < minLoanAmount && (
        <PageLayout title={lowerThanMinimumAmountTitle}>
          <IneligibleLoanAmountScreen
            currency={currency}
            scholarshipPeriodAmount={scholarshipPeriodAmount}
            sponsorshipPeriodAmount={sponsorshipPeriodAmount}
            loansPeriodAmount={loansPeriodAmount}
            handleClick={handleClick}
          />
        </PageLayout>
      )}
    </div>
  );
};

LoanAmountPage.propTypes = {
  currentLoanPeriod: loanPeriodAmountPropTypes.isRequired,
  period: periodPropTypes.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchFetchCapOnLoan: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  minLoanAmount: PropTypes.number.isRequired,
  queryMinLoanAmount: PropTypes.func.isRequired,
  eligibilityId: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  otherFunds: otherFundsPropTypes.isRequired,
  capOnLoan: capOnLoanPropTypes.isRequired,
  isFullTimeCourse: PropTypes.bool.isRequired,
  seenDepositPage: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  currentLoanPeriod: state.builder.ui.currentLoanPeriod,
  period: periodSelector(state),
  minLoanAmount: minLoanAmountSelector(state),
  eligibilityId: eligibilityIdSelector(state),
  currency: periodCurrencySelector(state),
  otherFunds: otherFundsSelector(state),
  capOnLoan: capOnLoanSelector(state),
  isFullTimeCourse: isFullTimeCourseSelector(state),
  seenDepositPage: fundsRequiredSelector(state),
  isLoading: loadingSelector(state),
});

const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  queryMinLoanAmount: queryMinLoanAmountRequest,
  dispatchFetchCapOnLoan: fetchCapOnLoan,
};

export { LoanAmountPage, mapStateToProps, mapDispatchToProps };

export default connect(mapStateToProps, mapDispatchToProps)(LoanAmountPage);
